/**
 * CDB-TQS API — Knowledge Areas
 */

import type {
  CdbTqsKnowledgeAreaListResponse,
  CdbTqsKnowledgeArea,
  CdbTqsKnowledgeAreaBase,
  CreateKnowledgeAreaPayload,
  KnowledgeAreaSizeTier,
} from "@/types/cdb-tqs-knowledge-areas";
import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

export const knowledgeAreasApi = {
  /**
   * Get all knowledge areas from CDB-TQS backend
   * @param activeOnly - If true, only return active knowledge areas (default: true)
   * @returns List of knowledge areas with document counts
   */
  async getKnowledgeAreas(
    activeOnly: boolean = true
  ): Promise<CdbTqsKnowledgeAreaListResponse> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas`,
      {
        method: "GET",
        params: { active_only: activeOnly },
      }
    );

    return response.data as CdbTqsKnowledgeAreaListResponse;
  },

  /**
   * Create a new knowledge area. Without `name` cdb_tqs derives a unique one
   * from `display_name` and returns it (cdb_tqs#2188).
   */
  async createKnowledgeArea(
    data: CreateKnowledgeAreaPayload
  ): Promise<CdbTqsKnowledgeArea> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );

    return response.data as CdbTqsKnowledgeArea;
  },

  /** The ID cdb_tqs would derive from `displayName` right now, e.g. `kundenservice_2`. */
  async getKnowledgeAreaNameSuggestion(displayName: string): Promise<string> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas/name-suggestion`,
      { method: "GET", params: { display_name: displayName } }
    );
    return (response.data as { name: string }).name;
  },

  /** The sizes a new area can take; `admin_only` marks Unbegrenzt. */
  async getKnowledgeAreaSizeTiers(): Promise<KnowledgeAreaSizeTier[]> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas/size-tiers`,
      { method: "GET" }
    );
    return (response.data as { tiers: KnowledgeAreaSizeTier[] }).tiers;
  },

  /**
   * Update an existing knowledge area
   * @param id - Knowledge area ID
   * @param data - Partial knowledge area data to update
   * @returns Updated knowledge area
   */
  async updateKnowledgeArea(
    id: string,
    data: Partial<CdbTqsKnowledgeAreaBase>
  ): Promise<CdbTqsKnowledgeArea> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    );

    return response.data as CdbTqsKnowledgeArea;
  },

  /**
   * Delete a knowledge area
   * @param id - Knowledge area ID
   */
  async deleteKnowledgeArea(id: string): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/knowledge-areas/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Get chunk usage for a knowledge area by name
   * @param name - Knowledge area name
   * @returns Chunk count, limit, usage percentage, and over-limit flag
   */
  async getKnowledgeAreaChunkUsage(name: string): Promise<{
    knowledge_area: string;
    chunk_count: number;
    chunk_limit: number | null;
    usage_percentage: number;
    is_over_limit: boolean;
  }> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/knowledge-areas/by-name/${encodeURIComponent(name)}/chunk-usage`,
      { method: "GET" }
    );
    return response.data;
  },
};
